import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  
  // User permissions (fetched from roles API)
  const [userPermissions, setUserPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load wallet data
      const walletRes = await api.get("/Wallet/my-wallet");
      setWallet(walletRes.data);

      // Load transaction history - try multiple endpoints
      let txData = [];
      try {
        const txRes = await api.get("/Transaction/my-transactions?limit=50");
        txData = txRes.data.transactions || txRes.data || [];
      } catch (e1) {
        try {
          const txRes = await api.get("/Wallet/transactions?limit=50");
          txData = txRes.data.transactions || txRes.data || [];
        } catch (e2) {
          console.log("Could not load transactions:", e2);
        }
      }
      setTransactions(txData);

      // Try to load notification count
      try {
        const notifRes = await api.get("/Notifications/unread-count");
        setNotificationCount(notifRes.data?.count || 0);
      } catch (e) {
        console.log("Could not load notification count:", e);
      }

      // Load user permissions/roles
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (storedUser.id) {
        try {
          const permRes = await api.get(`/Roles/users/${storedUser.id}/permissions`);
          setUserPermissions(permRes.data.permissionCodes || []);
          setUserRoles(permRes.data.roles || []);
        } catch (e) {
          console.log("Could not load user permissions:", e);
        }
      }

    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    
    if (!storedUser.id) {
      navigate("/user-login");
      return;
    }

    // Redirect admins to admin dashboard
    if (storedUser.isAdmin) {
      navigate("/admin");
      return;
    }

    // Redirect agents to agent dashboard
    if (storedUser.isAgent) {
      navigate("/agent-banking");
      return;
    }

    setUser(storedUser);
    loadDashboardData();
  }, [navigate, loadDashboardData]);

  const logout = () => {
    localStorage.clear();
    navigate("/user-login");
  };

  // Check if user has specific permission
  const hasPermission = (permCode) => {
    return userPermissions.includes(permCode);
  };

  // Check if user has a specific role
  const hasRole = (roleName) => {
    return userRoles.some(r => r.name?.toLowerCase() === roleName.toLowerCase());
  };

  // Check if user should see special features
  const hasSpecialAccess = () => {
    return hasPermission("RISK_VIEW") || 
           hasPermission("RISK_MANAGE") || 
           hasPermission("KYC_VIEW") ||
           hasPermission("KYC_APPROVE") ||
           hasPermission("TXN_VIEW") ||
           hasPermission("TXN_APPROVE") ||
           hasPermission("TXN_REVERSE") ||
           hasPermission("REPORT_VIEW") ||
           hasPermission("REPORT_GENERATE") ||
           hasPermission("REPORT_EXPORT") ||
           hasPermission("ACCT_VIEW") ||
           hasPermission("ACCT_RECONCILE") ||
           hasPermission("ACCT_JOURNAL") ||
           hasPermission("FLOAT_VIEW") ||
           hasPermission("FLOAT_MANAGE") ||
           hasPermission("FEE_VIEW") ||
           hasPermission("FEE_MANAGE") ||
           hasPermission("ROLE_VIEW") ||
           hasPermission("ROLE_ASSIGN") ||
           hasPermission("USER_VIEW") ||
           hasPermission("USER_CREATE") ||
           hasRole("Compliance Officer") ||
           hasRole("Branch Manager") ||
           hasRole("Auditor") ||
           hasRole("Financial Control") ||
           hasRole("Teller");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>💼 Dashboard</span>
        </div>
        <div style={styles.headerRight}>
          <button 
            onClick={() => navigate("/notifications")} 
            style={styles.iconBtn}
            title="Notifications"
          >
            🔔 {notificationCount > 0 && <span style={styles.badge}>{notificationCount}</span>}
          </button>
          <button 
            onClick={() => navigate("/kyc")} 
            style={styles.kycBtn}
          >
            📋 KYC
          </button>
          <button onClick={logout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>

      <main style={styles.main}>
        {/* User Info Card */}
        <div style={styles.userCard}>
          <div style={styles.userIcon}>👤</div>
          <div style={styles.userInfo}>
            <h2 style={styles.userName}>{user?.fullName || wallet?.fullName}</h2>
            <div style={styles.userMeta}>
              <span>Account Number</span>
              <span>Current Balance</span>
              <span>Status</span>
            </div>
            <div style={styles.userValues}>
              <span style={styles.accountNumber}>{wallet?.accountNumber || "N/A"}</span>
              <span style={styles.balance}>{formatCurrency(wallet?.balance)}</span>
              <span style={{ ...styles.statusBadge, backgroundColor: wallet?.status === "Active" ? "#10b981" : "#ef4444" }}>
                {wallet?.status || "Active"}
              </span>
            </div>
          </div>
        </div>

        {/* User Roles Display */}
        {userRoles.length > 0 && (
          <div style={styles.rolesBar}>
            <span style={styles.rolesLabel}>Your Roles:</span>
            {userRoles.map((role, idx) => (
              <span key={idx} style={styles.roleBadge}>{role.name}</span>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div style={styles.actionsGrid}>
          <button onClick={() => navigate("/transfer")} style={{ ...styles.actionBtn, backgroundColor: "#3b82f6" }}>
            💸 Transfer
          </button>
          <button onClick={() => navigate("/reports")} style={{ ...styles.actionBtn, backgroundColor: "#8b5cf6" }}>
            📊 Reports
          </button>
          <button onClick={() => navigate("/qr-receive")} style={{ ...styles.actionBtn, backgroundColor: "#10b981" }}>
            📱 QR Code
          </button>
        </div>

        {/* Role-Based Features - Special Access Section */}
        {hasSpecialAccess() && (
          <div style={styles.specialAccessSection}>
            <h3 style={styles.sectionTitle}>🔐 Special Access</h3>
            <p style={styles.specialAccessDesc}>Based on your assigned roles and permissions, you have access to the following features:</p>
            <div style={styles.specialActionsGrid}>
              {(hasPermission("RISK_VIEW") || hasPermission("RISK_MANAGE") || hasRole("Compliance Officer")) && (
                <button 
                  onClick={() => navigate("/risk-management")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#dc2626" }}
                >
                  🛡️ Risk Management
                </button>
              )}
              {(hasPermission("KYC_VIEW") || hasPermission("KYC_APPROVE") || hasRole("Compliance Officer")) && (
                <button 
                  onClick={() => navigate("/kyc-admin")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#f59e0b" }}
                >
                  📋 KYC Review
                </button>
              )}
              {(hasPermission("REPORT_GENERATE") || hasPermission("REPORT_VIEW") || hasRole("Compliance Officer") || hasRole("Auditor")) && (
                <button 
                  onClick={() => navigate("/reports")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#6366f1" }}
                >
                  📈 Generate Reports
                </button>
              )}
              {(hasPermission("TXN_APPROVE") || hasRole("Branch Manager")) && (
                <button 
                  onClick={() => navigate("/transaction-approvals")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#0ea5e9" }}
                >
                  ✅ Approve Transactions
                </button>
              )}
              {(hasPermission("ROLE_VIEW") || hasPermission("ROLE_ASSIGN")) && (
                <button 
                  onClick={() => navigate("/role-management")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#8b5cf6" }}
                >
                  👥 Role Management
                </button>
              )}
              {(hasPermission("ACCT_VIEW") || hasPermission("ACCT_RECONCILE") || hasPermission("ACCT_JOURNAL") || hasRole("Financial Control")) && (
                <button 
                  onClick={() => navigate("/accounting")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#059669" }}
                >
                  📒 Accounting
                </button>
              )}
              {(hasPermission("FLOAT_VIEW") || hasPermission("FLOAT_MANAGE") || hasPermission("ACCT_VIEW") || hasRole("Teller") || hasRole("Branch Manager") || hasRole("Financial Control")) && (
                <button 
                  onClick={() => navigate("/float-management")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#0891b2" }}
                >
                  💵 Agent Float
                </button>
              )}
              {(hasPermission("FEE_VIEW") || hasPermission("FEE_MANAGE")) && (
                <button 
                  onClick={() => navigate("/fees")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#7c3aed" }}
                >
                  💰 Fee Management
                </button>
              )}
              {(hasPermission("TXN_VIEW") || hasPermission("TXN_REVERSE")) && (
                <button 
                  onClick={() => navigate("/reversals")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#be185d" }}
                >
                  🔄 Reversals
                </button>
              )}
              {(hasPermission("USER_VIEW") || hasPermission("USER_CREATE")) && (
                <button 
                  onClick={() => navigate("/admin")} 
                  style={{ ...styles.specialBtn, backgroundColor: "#475569" }}
                >
                  👤 User Management
                </button>
              )}
            </div>
          </div>
        )}

        {/* Info Notice */}
        <div style={styles.infoNotice}>
          <span style={styles.infoIcon}>ℹ️</span>
          <span>You are a regular user. You can transfer money between accounts. For deposits and withdrawals, please contact an agent.</span>
        </div>

        {/* Recent Transactions */}
        <div style={styles.transactionsSection}>
          <h3 style={styles.sectionTitle}>Recent Transactions</h3>
          {transactions.length === 0 ? (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📭</span>
              <p>No transactions yet</p>
            </div>
          ) : (
            <div style={styles.transactionsList}>
              {transactions.slice(0, 10).map((tx, idx) => (
                <div key={idx} style={styles.txItem}>
                  <div style={styles.txLeft}>
                    <span style={{
                      ...styles.txIcon,
                      backgroundColor: tx.type === "Credit" ? "#d1fae5" : "#fee2e2",
                      color: tx.type === "Credit" ? "#059669" : "#dc2626"
                    }}>
                      {tx.type === "Credit" ? "↓" : "↑"}
                    </span>
                    <div>
                      <div style={styles.txDesc}>{tx.description || tx.type}</div>
                      <div style={styles.txDate}>{formatDate(tx.createdAt || tx.date)}</div>
                    </div>
                  </div>
                  <span style={{
                    ...styles.txAmount,
                    color: tx.type === "Credit" ? "#059669" : "#dc2626"
                  }}>
                    {tx.type === "Credit" ? "+" : "-"}{formatCurrency(tx.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f3f4f6",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: "20px"
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #e5e7eb",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  header: {
    backgroundColor: "#1e3a5f",
    color: "white",
    padding: "15px 30px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  logo: {
    fontSize: "24px",
    fontWeight: "700"
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "15px"
  },
  iconBtn: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    position: "relative",
    padding: "8px"
  },
  badge: {
    position: "absolute",
    top: "0",
    right: "0",
    backgroundColor: "#ef4444",
    color: "white",
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "10px"
  },
  kycBtn: {
    padding: "8px 16px",
    backgroundColor: "#f59e0b",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600"
  },
  logoutBtn: {
    padding: "8px 16px",
    backgroundColor: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600"
  },
  main: {
    padding: "30px",
    maxWidth: "900px",
    margin: "0 auto"
  },
  userCard: {
    backgroundColor: "#1e3a5f",
    borderRadius: "16px",
    padding: "25px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    color: "white",
    marginBottom: "20px"
  },
  userIcon: {
    fontSize: "50px",
    backgroundColor: "rgba(255,255,255,0.1)",
    padding: "15px",
    borderRadius: "50%"
  },
  userInfo: {
    flex: 1
  },
  userName: {
    margin: "0 0 15px",
    fontSize: "24px",
    fontWeight: "600"
  },
  userMeta: {
    display: "flex",
    gap: "40px",
    fontSize: "12px",
    opacity: 0.8,
    marginBottom: "5px"
  },
  userValues: {
    display: "flex",
    gap: "40px",
    alignItems: "center"
  },
  accountNumber: {
    fontFamily: "monospace",
    fontSize: "16px"
  },
  balance: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#10b981"
  },
  statusBadge: {
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
    color: "white"
  },
  rolesBar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 20px",
    backgroundColor: "#e0e7ff",
    borderRadius: "8px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  rolesLabel: {
    fontWeight: "600",
    color: "#3730a3"
  },
  roleBadge: {
    backgroundColor: "#4f46e5",
    color: "white",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "500"
  },
  actionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "15px",
    marginBottom: "25px"
  },
  actionBtn: {
    padding: "20px",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px"
  },
  specialAccessSection: {
    backgroundColor: "#fef3c7",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "25px",
    border: "2px solid #f59e0b"
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: "18px",
    fontWeight: "600",
    color: "#1e3a5f"
  },
  specialAccessDesc: {
    margin: "0 0 15px",
    fontSize: "13px",
    color: "#92400e"
  },
  specialActionsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px"
  },
  specialBtn: {
    padding: "15px 20px",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px"
  },
  infoNotice: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "15px 20px",
    backgroundColor: "#dbeafe",
    borderRadius: "8px",
    marginBottom: "25px",
    color: "#1e40af",
    fontSize: "14px"
  },
  infoIcon: {
    fontSize: "18px"
  },
  transactionsSection: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
  },
  emptyState: {
    textAlign: "center",
    padding: "40px",
    color: "#9ca3af"
  },
  emptyIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "10px"
  },
  transactionsList: {
    display: "flex",
    flexDirection: "column"
  },
  txItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 0",
    borderBottom: "1px solid #f3f4f6"
  },
  txLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  txIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: "bold"
  },
  txDesc: {
    fontWeight: "500",
    color: "#1f2937"
  },
  txDate: {
    fontSize: "12px",
    color: "#9ca3af"
  },
  txAmount: {
    fontWeight: "600",
    fontSize: "16px"
  }
};

